import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const PoliticaConfidentialitate = () => {
  return (
    <div className="min-h-screen bg-[#1a1a1a] text-amber-100">
      <SEOHead
        title="Politică de Confidențialitate — Casa Cartarum"
        description="Politica de confidențialitate și protecția datelor personale GDPR a site-ului Casa Cartarum."
        canonical="https://www.casacartarum.ro/politica-confidentialitate"
      />
      {/* Header with Back Button */}
      <div className="bg-gradient-to-b from-[#2a2520] to-[#1a1a1a] py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/#contact" 
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors mb-6"
            data-testid="back-to-contact"
          >
            <ArrowLeft size={20} />
            <span>Înapoi la Contact</span>
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-bold text-amber-100 mb-4">
            Politică de Confidențialitate
          </h1>
          <p className="text-amber-200/70 text-lg">
            Protecția Datelor - GDPR Compliant
          </p>
          <p className="text-amber-200/50 text-sm mt-2">
            Ultima actualizare: 15 februarie 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-invert prose-amber max-w-none">
          
          {/* Section 1: Introducere */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              1. INTRODUCERE
            </h2>
            
            <p className="text-amber-100/80 leading-relaxed mb-4">
              Asociația Culturală Dobrogenius operează Casa Cartarum și respectă confidențialitatea datelor dumneavoastră.
            </p>
            
            <div className="bg-blue-900/20 p-6 rounded-lg border border-blue-700/30">
              <h4 className="font-semibold text-blue-400 mb-3">Conformitate GDPR</h4>
              <p className="text-amber-100/70 text-sm">
                Această politică explică cum colectăm și protejăm datele în conformitate cu:
              </p>
              <ul className="list-disc list-inside space-y-1 text-amber-100/70 text-sm mt-3 ml-4">
                <li>Regulamentul General privind Protecția Datelor (GDPR) - UE 2016/679</li>
                <li>Legea nr. 190/2018 (România)</li>
              </ul>
            </div>
          </section>

          {/* Section 2: Operator Date */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              2. OPERATOR DE DATE
            </h2>
            
            <div className="bg-amber-900/20 p-6 rounded-lg border border-amber-800/30">
              <h3 className="text-xl font-semibold text-amber-100 mb-4">
                Asociația Culturală Dobrogenius
              </h3>
              <ul className="space-y-2 text-amber-100/80">
                <li>• Email: <a href="mailto:contact@dobrogenius.ro" className="text-amber-400">contact@dobrogenius.ro</a></li>
                <li>• Website: casa-cartarum.ro</li>
              </ul>
            </div>
          </section>

          {/* Section 3: Date Colectate */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              3. CE DATE COLECTĂM
            </h2>
            
            <h3 className="text-2xl font-semibold text-amber-100/90 mb-4">3.1 Date Colectate Automat</h3>
            
            <div className="bg-amber-900/20 p-5 rounded-lg border border-amber-800/30 mb-6">
              <h4 className="font-semibold text-amber-100 mb-3">Date tehnice:</h4>
              <ul className="space-y-1 text-amber-100/70 text-sm ml-4">
                <li>• Adresa IP</li>
                <li>• Tipul browserului</li>
                <li>• Sistemul de operare</li>
                <li>• Paginile vizitate</li>
              </ul>
              <p className="text-amber-100/60 text-sm mt-4">
                <strong>Scopul:</strong> Funcționarea tehnică, securitate, analize statistice
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-amber-100/90 mb-4">3.2 Cookie-uri</h3>
            <p className="text-amber-100/80 leading-relaxed mb-4">
              Utilizăm cookie-uri pentru funcționarea platformei și analize anonime.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-green-900/10 border border-green-700/30 rounded-lg p-4">
                <h4 className="font-semibold text-green-400 mb-2">Cookie-uri Esențiale</h4>
                <p className="text-amber-100/70 text-sm">Necesare pentru funcționare - nu necesită consimțământ</p>
              </div>
              <div className="bg-blue-900/10 border border-blue-700/30 rounded-lg p-4">
                <h4 className="font-semibold text-blue-400 mb-2">Cookie-uri Analiză</h4>
                <p className="text-amber-100/70 text-sm">Statistici anonime - necesită consimțământ</p>
              </div>
            </div>
          </section>

          {/* Section 4: Drepturile Dumneavoastră */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              4. DREPTURILE DUMNEAVOASTRĂ (GDPR)
            </h2>
            
            <div className="space-y-4">
              <div className="bg-amber-900/20 p-5 rounded-lg border-l-4 border-amber-600">
                <h4 className="font-semibold text-amber-100 mb-2">Dreptul de Acces (Art. 15)</h4>
                <p className="text-amber-100/70 text-sm">Puteți solicita o copie a datelor personale.</p>
              </div>
              
              <div className="bg-amber-900/20 p-5 rounded-lg border-l-4 border-amber-600">
                <h4 className="font-semibold text-amber-100 mb-2">Dreptul la Rectificare (Art. 16)</h4>
                <p className="text-amber-100/70 text-sm">Puteți solicita corectarea datelor inexacte.</p>
              </div>
              
              <div className="bg-amber-900/20 p-5 rounded-lg border-l-4 border-amber-600">
                <h4 className="font-semibold text-amber-100 mb-2">Dreptul la Ștergere (Art. 17)</h4>
                <p className="text-amber-100/70 text-sm">Puteți solicita ștergerea datelor ("dreptul de a fi uitat").</p>
              </div>
              
              <div className="bg-amber-900/20 p-5 rounded-lg border-l-4 border-amber-600">
                <h4 className="font-semibold text-amber-100 mb-2">Dreptul la Opoziție (Art. 21)</h4>
                <p className="text-amber-100/70 text-sm">Puteți să vă opuneți prelucrării în anumite condiții.</p>
              </div>
            </div>

            <div className="bg-blue-900/20 p-6 rounded-lg border border-blue-700/30 mt-6">
              <h4 className="font-semibold text-blue-400 mb-3">Cum Vă Exercitați Drepturile</h4>
              <p className="text-amber-100/70 text-sm mb-3">
                Trimiteți o solicitare la: <a href="mailto:contact@dobrogenius.ro" className="text-amber-400">contact@dobrogenius.ro</a>
              </p>
              <p className="text-amber-100/60 text-xs">
                Vom răspunde în maximum 30 de zile.
              </p>
            </div>
          </section>

          {/* Section 5: Securitatea Datelor */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              5. SECURITATEA DATELOR
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-900/10 border border-green-700/30 rounded-lg p-5">
                <h4 className="font-semibold text-green-400 mb-3">Măsuri Tehnice</h4>
                <ul className="space-y-1 text-amber-100/70 text-sm">
                  <li>• Conexiune HTTPS criptată</li>
                  <li>• Firewall și protecție</li>
                  <li>• Backup-uri regulate</li>
                  <li>• Monitorizare securitate</li>
                </ul>
              </div>
              
              <div className="bg-green-900/10 border border-green-700/30 rounded-lg p-5">
                <h4 className="font-semibold text-green-400 mb-3">Măsuri Organizatorice</h4>
                <ul className="space-y-1 text-amber-100/70 text-sm">
                  <li>• Acces restricționat</li>
                  <li>• Instruire personal</li>
                  <li>• Politici interne</li>
                  <li>• Evaluări periodice</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 6: Autoritatea de Supraveghere */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              6. AUTORITATEA DE SUPRAVEGHERE
            </h2>
            
            <div className="bg-red-900/10 border border-red-700/30 rounded-lg p-6">
              <h4 className="font-semibold text-red-400 mb-3">ANSPDCP</h4>
              <p className="text-amber-100/80 text-sm mb-3">
                Aveți dreptul de a depune o plângere la:
              </p>
              <div className="text-amber-100/70 text-sm space-y-2">
                <p><strong>Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal</strong></p>
                <p>• Website: <a href="https://www.dataprotection.ro" className="text-amber-400" target="_blank" rel="noopener noreferrer">www.dataprotection.ro</a></p>
                <p>• Email: anspdcp@dataprotection.ro</p>
                <p>• Telefon: +40 318 059 211</p>
              </div>
            </div>
          </section>

          {/* Section 7: Link to Terms */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              7. TERMENI ȘI CONDIȚII
            </h2>
            
            <p className="text-amber-100/80 leading-relaxed mb-4">
              Pentru detalii despre utilizarea platformei, consultați:
            </p>

            <Link 
              to="/termeni-conditii" 
              className="inline-block bg-amber-700 hover:bg-amber-600 text-white px-6 py-3 rounded-lg transition-colors font-semibold"
              data-testid="link-to-terms"
            >
              Vezi Termenii și Condițiile →
            </Link>
          </section>

          {/* Section 8: Contact */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              8. CONTACT
            </h2>
            
            <div className="bg-gradient-to-br from-amber-900/20 to-amber-800/10 p-8 rounded-lg border border-amber-700/30">
              <h3 className="text-2xl font-semibold text-amber-100 mb-4">
                Pentru întrebări despre confidențialitate:
              </h3>
              <div className="space-y-3 text-amber-100/80">
                <p className="flex items-center gap-3">
                  <span className="text-amber-400">📧</span>
                  <a href="mailto:contact@dobrogenius.ro" className="text-amber-300 hover:text-amber-200">
                    contact@dobrogenius.ro
                  </a>
                </p>
                <p className="flex items-center gap-3">
                  <span className="text-amber-400">📝</span>
                  <span className="text-sm">Subiect: "Întrebare GDPR"</span>
                </p>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="mt-16 pt-8 border-t border-amber-800/30 text-center">
            <p className="text-amber-100/60 text-sm mb-4">
              Ultima actualizare: 15 februarie 2026
            </p>
            <p className="text-amber-100/40 text-xs">
              © 2026 Asociația Culturală Dobrogenius
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PoliticaConfidentialitate;
